/**
 * Centralized API Endpoints
 * Organized by module for easy maintenance and scalability
 */

import { api, uploadFile } from './client';

/**
 * Authentication Endpoints
 */
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials, { skipAuth: true }),
  register: (userData) => api.post('/auth/register', userData, { skipAuth: true }),
  logout: () => api.post('/auth/logout'),
  refreshToken: (refreshToken) => api.post('/auth/refresh', { refresh_token: refreshToken }, { skipAuth: true }),
  validateToken: (token) => api.post('/auth/validate-token', { token }, { skipAuth: true }),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }, { skipAuth: true }),
  resetPassword: (token, password) => api.post('/auth/reset-password', { token, password }, { skipAuth: true }),
  changePassword: (currentPassword, newPassword) => api.post('/auth/change-password', { current_password: currentPassword, new_password: newPassword }),
  // changePasswordWithoutOld: (currentPassword, newPassword) => api.post('/user/me/password', { current_password: currentPassword, new_password: newPassword }),
  changePasswordWithoutOld: (data) => api.put('/users/me/password', data),

};

/**
 * User Endpoints
 */
export const userAPI = {
  getProfile: () => api.get('/users/profile'),
  updateProfile: (userData) => api.put('/users/profile', userData),
  getSettings: () => api.get('/users/settings'),
  updateSettings: (settings) => api.put('/users/settings', settings),
  getSecurityPhrase: () => api.get('/users/security-phrase'),
  updateSecurityPhrase: (phrase) => api.post('/users/security-phrase', { phrase }),
};

/**
 * CBT (Computer-Based Test) Endpoints
 */
export const cbtAPIold = {
  // CBT Management
  createCBT: (cbtData) => api.post('/cbt/create', cbtData),
  getMyCBTs: (params = {}) => api.get(`/cbt/my-cbts?${new URLSearchParams(params).toString()}`),
  getCBTList: (params = {}) => api.get(`/cbt/list?${new URLSearchParams(params).toString()}`),
  getCBTDetails: (cbtId) => api.get(`/cbt/${cbtId}`),
  updateCBT: (cbtId, cbtData) => api.put(`/cbt/${cbtId}`, cbtData),
  deleteCBT: (cbtId) => api.delete(`/cbt/${cbtId}`),
  publishCBT: (cbtId) => api.post(`/cbt/${cbtId}/publish`),
  archiveCBT: (cbtId) => api.post(`/cbt/${cbtId}/archive`),

  // Questions
  createQuestion: (cbtPassCode, questionData) => api.post(`/cbt/${cbtPassCode}/questions`, questionData),
  getQuestions: (cbtPassCode) => api.get(`/cbt/${cbtPassCode}/questions`),
  updateQuestion: (cbtPassCode, questionId, questionData) => api.put(`/cbt/${cbtPassCode}/questions/${questionId}`, questionData),
  deleteQuestion: (cbtPassCode, questionId) => api.delete(`/cbt/${cbtPassCode}/questions/${questionId}`),
  bulkCreateQuestions: (cbtPassCode, questionsData) => api.post(`/cbt/questions/bulk-create`, { cbt_pass_code: cbtPassCode, questions: questionsData }),

  // Student Test Access
  getPassCodeInfo: (passCode) => api.get(`/cbt/pass-code/${passCode}`, { skipAuth: true }),
  verifyPassCode: (passCode) => api.post(`/cbt/pass-code/verify`, { pass_code: passCode }, { skipAuth: true }),
  startTest: (passCode) => api.post(`/cbt/test/start`, { pass_code: passCode }),
  getTestSession: (sessionId) => api.get(`/cbt/test/session/${sessionId}`),
  submitAnswer: (sessionId, questionId, answerData) => api.post(`/cbt/test/${sessionId}/answer`, { question_id: questionId, ...answerData }),
  submitTest: (sessionId) => api.post(`/cbt/test/${sessionId}/submit`),
  getTestResults: (sessionId) => api.get(`/cbt/test/${sessionId}/results`),


  // 
   // For TestResult (overview)
  getStudentResults: (cbtId, params = {}) => 
    api.get(`/cbt/test-results/${cbtId}?${new URLSearchParams(params).toString()}`),

  // 11
  // For TestData (detailed student attempts)
  getStudentResult: (cbtId, studentId) => 
    api.get(`/cbt/student-results/${cbtId}/${studentId}`),

  // Manual score update
  updateScore: (data) => 
    api.post('/cbt/update-score', data),

  // Test Analytics & Results
  getTestAnalytics: (cbtId) => api.get(`/cbt/${cbtId}/analytics`),
    
  // Security & Transfer
  transferQuestions: (transferData) => api.post(`/cbt/transfer-questions`, transferData),
  importStudents: (cbtPassCode, studentData) => api.post(`/cbt/import-students`, { cbt_pass_code: cbtPassCode, students: studentData }),
  createSecurityPhrase: (phraseData) => api.post(`/cbt/security-phrases/create`, phraseData),
  getSecurityPhrases: (cbtPassCode) => api.get(`/cbt/security-phrases?cbt_pass_code=${cbtPassCode}`),
};

/**
 * Admin Endpoints
 */
export const adminAPI = {
  getDashboard: () => api.get('/admin/dashboard'),
  getStats: () => api.get('/admin/stats'),
  
  // User Management
  getAllUsers: (params = {}) => api.get(`/admin/users?${new URLSearchParams(params).toString()}`),
  getUserDetails: (userId) => api.get(`/admin/users/${userId}`),
  updateUser: (userId, userData) => api.put(`/admin/users/${userId}`, userData),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),

  // CBT Management
  getAllCBTs: (params = {}) => api.get(`/admin/cbts?${new URLSearchParams(params).toString()}`),
  approveCBT: (cbtId) => api.post(`/admin/cbts/${cbtId}/approve`),
  rejectCBT: (cbtId, reason) => api.post(`/admin/cbts/${cbtId}/reject`, { reason }),
};

/**
 * Payment & Billing Endpoints
 */
export const paymentAPI = {
  // Plans
  getPlans: () => api.get('/payments/plans'),

  // Invoices
  createInvoice: (invoiceData) => api.post('/payments/invoice', invoiceData),
  getInvoice: (invoiceId) => api.get(`/payments/invoice/${invoiceId}`),
  getInvoices: (params = {}) => api.get(`/payments/invoices?${new URLSearchParams(params).toString()}`),
  verifyPayment: (invoiceId, verificationData) => api.post(`/payments/verify/${invoiceId}`, verificationData),

  // Bank Transfer
  confirmBankTransfer: (confirmationData) => api.post('/payments/bank-transfer/confirm', confirmationData),

  // Subscription
  checkSubscription: (userId) => api.get(`/payments/subscription/${userId}`),
  addSubscriptionUser: (ownerId, userData) => api.post(`/payments/subscription/${ownerId}/users`, userData),
  useSubscriptionToken: (token) => api.get(`/payments/subscription/token/${token}`),

  // History & Stats
  getPaymentHistory: (params = {}) => api.get(`/payments/history?${new URLSearchParams(params).toString()}`),
  getPaymentStats: () => api.get('/payments/stats'),
  getUserPaymentSummary: () => api.get('/payments/user-summary'),

  // Webhook
  paystackWebhook: (payload) => api.post('/payments/webhook/paystack', payload, { skipAuth: true }),
};




/** 
 * Subscription Endpoints (Updated to match backend)
 */
export const subscriptionAPI = {
  // Plans
  getPlans: () => api.get('/subscription/plans'),
  getPlan: (planId) => api.get(`/subscription/plans/${planId}`),
 getAllInvitations: () => api.get('/subscription/all-invitations'),
  // User Subscriptions
  getUserSubscription: () => api.get('/subscription/my-subscription'),
  subscribeToPlan: (planId) => api.post(`/subscription/subscribe/${planId}`),
  upgradeSubscription: (planId) => api.post(`/subscription/upgrade/${planId}`),
  cancelSubscription: () => api.post('/subscription/cancel'),
removeSubscriptionUser : (email) => api.delete(`/subscription/user-by-email/${encodeURIComponent(email)}`),

  // Subscription Users Management (Owner side)
  getSubscriptionUsers: () => api.get('/subscription/users'),
  addSubscriptionUser: (email) => api.post('/subscription/add-user', { email }),
  // removeSubscriptionUser: (userId) => api.delete(`/subscription/users/${userId}`),
  
  // Subscription Tokens
  generateToken: () => api.post('/subscription/generate-token'),
  acceptToken: (token) => api.post('/subscription/accept', { token }),
  revokeToken: (tokenId) => api.delete(`/subscription/tokens/${tokenId}`),

  // User Invitations (User side)
  getInvitations: () => api.get('/subscription/invitations'),
  acceptInvitation: (invitationId) => api.post(`/subscription/invitation/${invitationId}/accept`),
  rejectInvitation: (invitationId) => api.delete(`/subscription/invitations/${invitationId}`),
  
  // Joined Subscriptions
  getJoinedSubscriptions: () => api.get('/subscription/joined'),
  leaveSubscription: (subscriptionId) => api.post(`/subscription/leave/${subscriptionId}`),
  
  // User Status Management
  deactivateUser: (userId) => api.post(`/subscription/deactivate/${userId}`),
  reactivateUser: (userId) => api.post(`/subscription/reactivate/${userId}`)
};

// };

/**
 * Asset/File Endpoints
 */
export const assetAPI = {
  uploadFile: (file, additionalData) => uploadFile('/assets/upload', file, additionalData),
  getFile: (fileId) => api.get(`/assets/${fileId}`),
  deleteFile: (fileId) => api.delete(`/assets/${fileId}`),
  getImageLibrary: (params = {}) => api.get(`/assets/library?${new URLSearchParams(params).toString()}`),
  uploadImage: (file) => uploadFile('/assets/upload/image', file),
};

/**
 * API Key Endpoints
 */
export const apiKeyAPI = {
  createKey: (keyData) => api.post('/api-keys/create', keyData),
  getKeys: () => api.get('/api-keys'),
  getKey: (keyId) => api.get(`/api-keys/${keyId}`),
  revokeKey: (keyId) => api.delete(`/api-keys/${keyId}`),
  regenerateKey: (keyId) => api.post(`/api-keys/${keyId}/regenerate`),
};

/**
 * Health/Status Endpoints
 */
export const healthAPI = {
  checkHealth: () => api.get('/health', { skipAuth: true }),
  getStatus: () => api.get('/status', { skipAuth: true }),
};


// Add to api/index.js

/**
 * Subscription Management Endpoints
 */
// export const subscriptionAPI = {
 
// };





// Add this to the cbtAPI section after the bulkCreateQuestions endpoint

/** 
 * CBT (Computer-Based Test) Endpoints
 */
export const cbtAPI = {

  // Inside cbtAPI object, add:
//  exportOfflinePackage: (cbtPassCode) => 
//     api.get(`/cbt/${cbtPassCode}/offline-package`, { 
//       responseType: 'blob' 
//     }),

    exportOfflinePackage: (cbtPassCode) =>
    api.get(`/cbt/${cbtPassCode}/offline-package`, {
        responseType: "blob"
    }),
    
    downloadExcelTemplate: async () => {
        return await api.get('/upload_docx/excel-template', {
            responseType: 'blob',
            headers: {
                'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            }
        });
    },

// Answers (single)
createAnswer: (answerData) => api.post('/cbt/answers', answerData),
updateAnswer: (answerId, answerData) => api.put(`/cbt/answers/${answerId}`, answerData),
deleteAnswer: (answerId) => api.delete(`/cbt/answers/${answerId}`),
  // CBT Management
  createCBT: (cbtData) => api.post('/cbt/create', cbtData),
  getMyCBTs: (params = {}) => api.get(`/cbt/my-cbts?${new URLSearchParams(params).toString()}`),
  getCBTList: (params = {}) => api.get(`/cbt/list?${new URLSearchParams(params).toString()}`),
  getCBTDetails: (cbtId) => api.get(`/cbt/${cbtId}`),
  updateCBT: (cbtId, cbtData) => api.put(`/cbt/${cbtId}`, cbtData),
  deleteCBT: (cbtId) => api.delete(`/cbt/${cbtId}`),
  publishCBT: (cbtId) => api.post(`/cbt/${cbtId}/publish`),
  archiveCBT: (cbtId) => api.post(`/cbt/${cbtId}/archive`),

  // Questions
  createQuestion: (cbtPassCode, questionData) => api.post(`/cbt/${cbtPassCode}/questions`, questionData),
  getQuestions: (cbtPassCode) => api.get(`/cbt/${cbtPassCode}/questions`),
  updateQuestion: (cbtPassCode, questionId, questionData) => api.put(`/cbt/${cbtPassCode}/questions/${questionId}`, questionData),
  deleteQuestion: (cbtPassCode, questionId) => api.delete(`/cbt/${cbtPassCode}/questions/${questionId}`),
  // bulkCreateQuestions: (cbtPassCode, questionsData) => api.post(`/cbt/questions/bulk-create`, { cbt_pass_code: cbtPassCode, questions: questionsData }),
  
// In your api.js file, update the bulkCreateQuestions function:
bulkCreateQuestions: (cbtPassCode, questionsData) => 
    api.post(`/cbt/questions/bulk-create`, { 
        cbt_pass_code: cbtPassCode, 
        questions: questionsData 
    }),



uploadQuestionsFile: (cbtPassCode, file) => {
  return uploadFile(
    "/upload_docx/upload",
    file,
    {
      cbt_pass_code: cbtPassCode,
    }
  );
},
  // For TestResult (overview)
  getStudentResults: (cbtId, params = {}) => 
    api.get(`/cbt/test-results/${cbtId}?${new URLSearchParams(params).toString()}`),

  // 11
  // For TestData (detailed student attempts)
  getStudentResult: (cbtId, studentId) => 
    api.get(`/cbt/student-results/${cbtId}/${studentId}`),

  // Manual score update
  updateScore: (data) => 
    api.post('/cbt/update-score', data),
  

  // Student Test Access
  getPassCodeInfo: (passCode) => api.get(`/cbt/pass-code/${passCode}`, { skipAuth: true }),
  verifyPassCode: (passCode) => api.post(`/cbt/pass-code/verify`, { pass_code: passCode }, { skipAuth: true }),
  startTest: (passCode) => api.post(`/cbt/test/start`, { pass_code: passCode }),
  getTestSession: (sessionId) => api.get(`/cbt/test/session/${sessionId}`),
  submitAnswer: (sessionId, questionId, answerData) => api.post(`/cbt/test/${sessionId}/answer`, { question_id: questionId, ...answerData }),
  submitTest: (sessionId) => api.post(`/cbt/test/${sessionId}/submit`),
  getTestResults: (sessionId) => api.get(`/cbt/test/${sessionId}/results`),

  // Test Analytics & Results
  getTestAnalytics: (cbtId) => api.get(`/cbt/${cbtId}/analytics`),
  getStudentResults: (cbtId, params = {}) => api.get(`/cbt/test-results/${cbtId}?${new URLSearchParams(params).toString()}`),

  
  // Security & Transfer
  transferQuestions: (transferData) => api.post(`/cbt/transfer-questions`, transferData),


importStudents: (cbtPassCode, studentData) =>
  api.post(
    '/cbt/import-students',
    studentData,                // body = LIST
    {
      params: { cbt_pass_code: cbtPassCode } // query param
    }
  ),
getImportedStudents: (cbtPassCode) => api.get(`/cbt/${cbtPassCode}/imported-students`),
 deleteImportedStudent: (studentId) => api.delete(`/cbt/imported-students/${studentId}`),
  


  bulkDeleteImportedStudents: (studentIds) =>
  api.post('/cbt/imported-students/bulk-delete', {
    student_ids: studentIds
  }),

deleteSecurityPhrase: (id) =>
  api.delete(`/cbt/security-phrases/${id}`),


  createSecurityPhrase: (phraseData) => api.post(`/cbt/security-phrases/create`, phraseData),
  getSecurityPhrases: (cbtPassCode) => api.get(`/cbt/security-phrases?cbt_pass_code=${cbtPassCode}`),
};

/**
 * Billing Endpoints
 */
export const billingAPI = {
  getSummary: () => api.get('/billing/summary'),
  getPlans: () => api.get('/billing/plans'),
  getProducts: (activeOnly = true) => api.get(`/billing/products?active_only=${activeOnly}`),
  purchaseCredits: (productId, currency = 'NGN') => 
    api.post('/billing/purchase-credits', { product_id: productId, currency }),
  switchPaymentModel: (newModel) => 
    api.post('/billing/switch-model', { new_model: newModel }),
  updateAutoTopup: (enabled, threshold, amount) => 
    api.put('/billing/auto-topup', { enabled, threshold, amount }),
  getUsage: (resourceType) => api.get(`/billing/usage/${resourceType}`),
};

export default {
  authAPI,
  userAPI,
  cbtAPI,
  adminAPI,
  paymentAPI,
  subscriptionAPI,
  assetAPI,
  apiKeyAPI,
  healthAPI,
};

